/**
 * OpenAI Ads 計測ピクセル (oaiq) — storefront イベント送信
 *
 * 正本: https://developers.openai.com/ads/measurement-pixel
 * 初期化は snippets/openai-ads-pixel.liquid が head 上部で1回だけ行う。本ファイルは
 * その後のイベント送信のみを担当する(init を二重に呼ばないこと)。
 *
 * 送るイベント:
 *   page_viewed      … 全ページ
 *   contents_viewed  … 商品ページ
 *   items_added      … /cart/add.js 成功時 + カート数量の増加(change/update.js)
 *   checkout_started … チェックアウト遷移操作時
 *
 * 送らないイベント:
 *   order_created … チェックアウト/サンクスページではテーマJSが動かないため、
 *                   サーバ側 Conversions API で送る(dedupe の event_id = 注文ID)。
 *
 * 帰属(oppref): SDK がランディングURLから __oppref を first-party cookie に保存する。
 * サーバ側 CAPI に引き継ぐため、生値のまま cart attribute `_oai_oppref` に控える
 * (docs: oppref は不透明値。decode/正規化/加工しない)。
 */
(function () {
  'use strict';

  var cfg = window.__oaiAds || {};
  var CUR = cfg.currency || 'JPY';
  var ROOT = cfg.routeRoot || '/';
  var DEBUG = !!cfg.debug;

  // ISO 4217 の minor unit が 0 桁の通貨。Shopify は JPY でも金額を x100 で持つため、
  // これらの通貨は /100 して整数の「円」にする(¥5,000 → 500000 → 5000)。
  var ZERO_DECIMAL = { JPY: 1, KRW: 1, VND: 1, CLP: 1, ISK: 1 };

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[oaiAds]'].concat([].slice.call(arguments)));
  }

  function measure(name, data, opts) {
    if (typeof window.oaiq !== 'function') return;
    try {
      if (opts) window.oaiq('measure', name, data, opts);
      else window.oaiq('measure', name, data);
      log(name, data);
    } catch (e) {
      /* 計測失敗で購買導線を止めない */
    }
  }

  function amount(sub) {
    if (typeof sub !== 'number' || !isFinite(sub)) return undefined;
    return ZERO_DECIMAL[CUR] ? Math.round(sub / 100) : Math.round(sub);
  }

  /** data に金額を載せる(不明なら currency ごと省く。amount 有 → currency 必須) */
  function withAmount(data, sub) {
    var a = amount(sub);
    if (a !== undefined && a >= 0) {
      data.amount = a;
      data.currency = CUR;
    }
    return data;
  }

  /** contents[] の1要素。documented field のみ使う(id/name/content_type/quantity/amount/currency) */
  function content(id, name, qty, unitSub) {
    var c = { id: String(id == null ? '' : id), content_type: 'product', quantity: qty || 1 };
    if (name) c.name = String(name).slice(0, 200);
    return withAmount(c, unitSub);
  }

  /** AJAX API / Liquid 双方の line item 形状を1つに正規化 */
  function lineOf(it) {
    return {
      q: it.quantity || 0,
      id: it.variant_id || it.id,
      title: it.product_title || it.title,
      unit: typeof it.final_price === 'number' ? it.final_price : it.price
    };
  }

  function mapLines(items) {
    var m = {};
    for (var i = 0; i < (items || []).length; i++) {
      if (items[i] && items[i].key) m[items[i].key] = lineOf(items[i]);
    }
    return m;
  }

  // カートの直近スナップショット(key -> line)。数量の「増加」検出に使う。
  var snap = cfg.cart && cfg.cart.items ? mapLines(cfg.cart.items) : null;

  /* ------------------------------------------------------------------ *
   * oppref → cart attribute
   * ------------------------------------------------------------------ */
  var stampedOppref = (cfg.cart && cfg.cart.attr_oppref) || null;
  var stampInFlight = false;

  function rawCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
    return m ? m[1] : null; // decode しない(不透明値のまま扱う)
  }

  function stampOppref(hasItems) {
    if (!hasItems || stampInFlight) return;
    var v = rawCookie('__oppref');
    if (!v || v === stampedOppref) return;
    stampInFlight = true;
    try {
      fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _oai_oppref: v } }),
        keepalive: true
      })
        .then(function () {
          stampedOppref = v;
          log('oppref stamped');
        })
        .catch(function () {})
        .then(function () {
          stampInFlight = false;
        });
    } catch (e) {
      stampInFlight = false;
    }
  }

  /* ------------------------------------------------------------------ *
   * 照合情報(advanced matching)
   * user は init に渡す。measure には載せない(docs の要件)。
   * pixelId は基礎 init 済みなので繰り返さない。
   * ------------------------------------------------------------------ */
  if (cfg.user && typeof window.oaiq === 'function') {
    try {
      window.oaiq('init', { user: cfg.user });
      log('init user', Object.keys(cfg.user));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * イベント
   * ------------------------------------------------------------------ */
  measure('page_viewed', { type: 'contents' });

  if (cfg.product && cfg.product.id) {
    var p = cfg.product;
    measure(
      'contents_viewed',
      withAmount({ type: 'contents', contents: [content(p.id, p.title, 1, p.price)] }, p.price)
    );
  }

  function emitItemsAdded(contents, totalSub) {
    if (!contents.length) return;
    measure('items_added', withAmount({ type: 'contents', contents: contents }, totalSub));
  }

  /** カート全体JSONを直前スナップショットと突き合わせ、増加分だけ返す */
  function diffAdded(prev, cart) {
    var added = [];
    var total = 0;
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var before = prev && prev[it.key] ? prev[it.key].q : 0;
      var delta = (it.quantity || 0) - before;
      if (delta > 0) {
        var l = lineOf(it);
        added.push(content(l.id, l.title, delta, l.unit));
        total += (l.unit || 0) * delta;
      }
    }
    return { contents: added, total: total };
  }

  function onCartResponse(url, json) {
    if (!json || typeof json !== 'object') return;
    var path = String(url).split('?')[0];

    // /cart/add.js のレスポンスは「追加された行」のみ(カート全体ではない)
    if (/\/cart\/add(\.js)?$/.test(path)) {
      var items = json.items && json.items.length ? json.items : json.id ? [json] : [];
      var contents = [];
      var total = 0;
      for (var i = 0; i < items.length; i++) {
        var l = lineOf(items[i]);
        contents.push(content(l.id, l.title, l.q, l.unit));
        total += typeof items[i].final_line_price === 'number' ? items[i].final_line_price : (l.unit || 0) * l.q;
      }
      emitItemsAdded(contents, total);
      syncSnapshot(); // 直後の全体JSONで二重計上しないよう基準を更新
      return;
    }

    // ここから先はカート全体JSONのみ扱う
    if (typeof json.item_count !== 'number' || !json.items) return;

    // 明示的な数量変更のみ差分計上する(/cart.js の単なる再取得では発火させない)
    if (/\/cart\/(change|update)(\.js)?$/.test(path)) {
      var d = diffAdded(snap, json);
      emitItemsAdded(d.contents, d.total);
    }
    snap = mapLines(json.items);
    stampOppref(json.item_count > 0);
  }

  function syncSnapshot() {
    try {
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (c) {
          if (c && c.items) {
            snap = mapLines(c.items);
            stampOppref(c.item_count > 0);
          }
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* ---- fetch / XHR フック(全ての追加経路を1箇所で拾う) ---- */
  var isCartUrl = function (u) {
    return /\/cart(\/(add|change|update)(\.js)?|\.js)(\?|$)/.test(String(u));
  };

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var res = origFetch.apply(this, arguments);
      if (isCartUrl(url)) {
        res
          .then(function (r) {
            if (!r || !r.ok) return;
            r.clone()
              .json()
              .then(function (j) {
                onCartResponse(url, j);
              })
              .catch(function () {});
          })
          .catch(function () {});
      }
      return res;
    };
  }

  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__oaiUrl = url;
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (isCartUrl(xhr.__oaiUrl || '')) {
        xhr.addEventListener('load', function () {
          if (xhr.status < 200 || xhr.status >= 300) return;
          try {
            onCartResponse(xhr.__oaiUrl, JSON.parse(xhr.responseText));
          } catch (e) {}
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ---- checkout_started ---- */
  var checkoutFired = false;
  function fireCheckoutStarted() {
    if (checkoutFired) return;
    checkoutFired = true;
    var contents = [];
    var total = 0;
    for (var k in snap) {
      if (!Object.prototype.hasOwnProperty.call(snap, k)) continue;
      var l = snap[k];
      contents.push(content(l.id, l.title, l.q, l.unit));
      total += (l.unit || 0) * l.q;
    }
    var data = { type: 'contents' };
    if (contents.length) data.contents = contents;
    measure('checkout_started', withAmount(data, total));
    stampOppref(contents.length > 0);
  }

  var CHECKOUT_SEL = '[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"], .shopify-payment-button__button';
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(CHECKOUT_SEL)) fireCheckoutStarted();
    },
    true
  );
  document.addEventListener(
    'submit',
    function (e) {
      var f = e.target;
      if (f && f.querySelector && f.querySelector('[name="checkout"]')) fireCheckoutStarted();
    },
    true
  );

  // 既にカートがある状態で入ってきた場合の取りこぼし対策。
  // SDK が cookie を書くのは init 直後の非同期処理のため、少し待ってからも試す。
  if (cfg.cart && cfg.cart.item_count > 0) {
    stampOppref(true);
    setTimeout(function () {
      stampOppref(true);
    }, 2500);
  }
})();
